<template>
  <v-calendar-compat
    ref="veeCalendar"
    class="c-calendar"
    :class="{ '-alert': alert, '-brand': brand, '-wide': wide, '-narrow': !wide }"
    :style="{ '--columns': computedColumns }"
    :value="value"
    :mode="mode"
    :max-date="computedMaxDate"
    :min-date="computedMinDate"
    :max-page="computedMaxPage"
    :min-page="computedMinPage"
    :disabled-dates="disabledDates"
    :available-dates="computedAvailableDates"
    :columns="computedColumns"
    :rows="computedRows"
    :attributes="attributes"
    :drag-attribute="dragAttr"
    :from-page="fromPage"
    nav-visibility="hidden"
    is-inline
    is-expanded
    v-bind="datePickerAttrs"
    @input="onInput"
    @drag="$emit('drag', $event)"
    @dayclick="onDayClick"
  />
</template>

<script>
import VCalendarCompat from './fragments/VCalendarCompat'

/**
 * A simple but felxible datepicker.
 */
export default {
  name: 'CCalendar',

  components: { VCalendarCompat },

  emits: [ 'input', 'drag', 'preselect' ],

  props: {
    /**
     * The value of the field.
     * set to a Date type when mode is set to 'single'
     * @type { Date } }
     *
     * set to an Array of Dates when mode is set to 'multiple':
     * @type { Date[] }
     *
     * set to an Object with the following format when mode is 'range':
     * @type { { start: Date, end: Date } }
     */
    value: {
      type: [ Date, Array, Object, String ],
      default: null,
    },

    /**
    * The month and the year to open
    * example: { month: 3, year: 2018 }
    * @type { Object }
    */
    fromPage: {
      type: Object,
      default: null,
    },

    /**
     * The maximum date available to be picked.
     */
    maxDate: Date,

    /**
     * The minimum date available to be picked.
     */
    minDate: Date,

    /**
     * The selection mode.
     * @values 'single', 'multiple', 'range'
     *
     * 'single' for a single date output.
     * 'multiple' for an array of dates output.
     * 'range' for an Object with the following format output:
     * @type { { start: Date, end: Date } }
     */

    mode: {
      type: String,
      default: 'single',
      validator: mode => [ 'single', 'multiple', 'range' ].includes(mode)
    },

    /**
     * The number of panes to be displayed
     * @values 1, 2, 3
     */
    panes: {
      type: Number,
      default: 1,
      validator: c => [ 1, 2, 3 ].includes(c)
    },

    /**
     * Display the panes vertically when set to true
     */
    vertical: Boolean,

    /**
     * The list of events to be displayed as 'dots' in the calendar.
     * e.g. - holidays, scheduled meetings, etc.
     * @type { { date: Date, label: string, color: string }[] }
     */
    events: {
      type: Array,
      validator: events => events.every(({ date, label }) => {
        const hasDateAndLabel = !!date && !!label
        const nameIsString = typeof label === 'string'
        const dateIsDate = date instanceof Date

        return hasDateAndLabel && nameIsString && dateIsDate
      }),
      default: () => [],
    },

    /**
     * A date expression of available dates in the calendar
     * Check https://vcalendar.io/date-expressions.html
     */
    availableDates: {
      type: Object,
      default: () => null,
    },

    /**
     * A date expression of disabled dates in the calendar
     * Check https://vcalendar.io/date-expressions.html
     */
    disabledDates: {
      type: [ Object, Array ],
      default: () => [],
    },

    /**
     * Set the days highlight colors as the alert color map (usually yellow)
     */
    alert: Boolean,

    /**
     * Paints the event dots with the client's brand color instead of the
     * secondary (convenia-green) color.
     */
    brand: Boolean,

    /**
     * Set the default pane width (218px - narrow) to (256px - wide)
     */
    wide: Boolean,

  },

  data: () => ({ preselectStart: null }),

  computed: {
    controller () {
      return this.$refs.veeCalendar?.getController?.() || null
    },

    attributes () {
      return (this.events || []).map(({ date, label }) => ({
        dot: true,
        dates: [ date ],
        popover: { hideIndicator: true, label },
      }))
    },

    computedColumns () {
      return this.vertical ? 1 : this.panes
    },

    computedRows () {
      return this.vertical ? this.panes : 1
    },

    // minDate and maxDate don't work together with availableDates ಠ_ಠ
    computedAvailableDates () {
      return this.availableDates
        && {
          ...this.availableDates,
          ...(this.minDate ? { start: this.minDate } : {}),
          ...(this.maxDate ? { end: this.maxDate } : {})
        }
    },

    computedMaxDate () {
      return !this.availableDates ? this.maxDate : null
    },

    computedMinDate () {
      return !this.availableDates ? this.minDate : null
    },

    computedMaxPage () {
      if (!this.availableDates || !this.maxDate) return null

      const date = this.$date(this.maxDate)
      return { month: date.month() + 1, year: date.year() }
    },

    computedMinPage () {
      if (!this.availableDates || !this.minDate) return null

      const date = this.$date(this.minDate)
      return { month: date.month() + 1, year: date.year() }
    }
  },

  methods: {
    onInput (event) {
      if (event !== null) this.$emit('input', event)
    },

    onDayClick ({ date }) {
      if (this.preselectStart) {
        this.$emit('preselect', { start: this.preselectStart, end: date })
        this.preselectStart = null
      } else {
        this.preselectStart = date
        this.$emit('preselect', { start: date })
      }
    },

    checkDisabledMonth () {
      const picker = this.$refs.veeCalendar
      const days = (this.controller?.pages || []).flatMap(({ days }) => days)
      const anyValidDays = picker && days.some(({ date }) => picker.dateIsValid(date))

      if (!anyValidDays) this.controller?.moveNext?.()
    }
  },

  mounted () {
    this.checkDisabledMonth()
  },

  created () {
    this.datePickerAttrs = {
      locale: 'pt-BR',
      firstDayOfWeek: 1,
      masks: {
        weekdays: 'WWW',
        navMonths: 'MMM',
        dayPopover: 'DD/MM/YYYY'
      },
      theme: { container: { light: '' } }
    }

    this.dragAttr = { highlight: true }
  }
}
</script>

<style lang="scss">
@import './style.scss';
</style>
